import { motion } from "framer-motion";
import {
  BarChart,
  CheckCircle,
  CreditCard,
  Mail,
  Settings,
  ShieldCheck,
  UserPlus,
  Users,
} from "lucide-react";
import React, { useEffect } from "react";
import { Card, CardContent } from "../../ui/cards";
import { Helmet } from "react-helmet";

const steps = [
  {
    icon: <ShieldCheck className="h-10 w-10 text-blue-600" />,
    title: "Company Signup",
    description: "Create your company's account by signing up on the platform.",
  },
  {
    icon: <CreditCard className="h-10 w-10 text-rose-600" />,
    title: "Choose a Subscription Plan",
    description:
      "Select a plan that fits your organization’s size and assessment needs",
  },
  {
    icon: <Settings className="h-10 w-10 text-green-600" />,
    title: "Admin Dashboard Setup",
    description:
      "Admins access their dashboard and configure company settings, manage teams, and preferences",
  },
  {
    icon: <Users className="h-10 w-10 text-indigo-600" />,
    title: "Add Managers",
    description:
      "Invite your managers by email and assign them departments or employee groups",
  },
  {
    icon: <UserPlus className="h-10 w-10 text-purple-600" />,
    title: "Add Employees",
    description:
      "Send assessments to existing employees or new hires by simply entering their email addresses",
  },
  {
    icon: <Mail className="h-10 w-10 text-yellow-500" />,
    title: "Assessees Get Notified",
    description:
      "Each person taking the assessment receives an email with their unique assessment link to complete the assessment",
  },
  {
    icon: <CheckCircle className="h-10 w-10 text-pink-600" />,
    title: "Assessee Completes Assessment",
    description: "Assessee completes scenario-based questions",
  },
  {
    icon: <BarChart className="h-10 w-10 text-teal-600" />,
    title: "View Reports & Insights",
    description:
      "Admins and managers view results, trait analysis, and behavior patterns in clear visual reports",
  },
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0 },
};

const HowToUse = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 mt-4 max-md:mt-0 overflow-x-hidden">
      <Helmet>
        <title>How Selfsync’s AI-Powered Talent Assessment Works</title>
        <meta
          name="description"
          content="Discover how Selfsync’s AI-powered talent assessment platform helps you evaluate, develop, and empower teams through real-life behavioral insights."
        />
        	<link rel="canonical" href="https://selfsync.ai/how-it-works" />
      </Helmet>
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-20 text-center max-w-4xl mx-auto"
      >
        <h1 className="text-5xl max-md:text-3xl font-extrabold mt-10 bg-gradient-to-r from-purple-600 to-cyan-400 text-transparent bg-clip-text mb-6 leading-tight">
          Empower Your Team with Smart, Insightful Assessments
        </h1>
        <p className="text-gray-600 text-lg leading-relaxed">
          From onboarding to ongoing development, our intuitive platform helps
          you truly understand your people. Identify strengths, highlight growth
          areas, and make informed decisions using real-life behavioral data.
        </p>
      </motion.div>

      {/* Features Overview */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mb-20 grid grid-cols-1 md:grid-cols-3 gap-10 text-center"
      >
        <div className="p-6 bg-white/40 backdrop-blur-md border border-white/30 shadow-md rounded-2xl hover:shadow-lg transition">
          <h3 className="text-xl font-semibold text-gray-800 mb-3">
            📊 Behavioral Analysis
          </h3>
          <p className="text-gray-600 text-base">
            Get deep insight into personality, communication styles,
            decision-making and more.
          </p>
        </div>
        <div className="p-6 bg-white/40 backdrop-blur-md border border-white/30 shadow-md rounded-2xl hover:shadow-lg transition">
          <h3 className="text-xl font-semibold text-gray-800 mb-3">
            🧠 Scenario-Based Evaluations
          </h3>
          <p className="text-gray-600 text-base">
            Real-life situations help reveal natural strengths without feeling
            like a test.
          </p>
        </div>
        <div className="p-6 bg-white/40 backdrop-blur-md border border-white/30 shadow-md rounded-2xl hover:shadow-lg transition">
          <h3 className="text-xl font-semibold text-gray-800 mb-3">
            📥 Manager-Ready Reports
          </h3>
          <p className="text-gray-600 text-base">
            Instantly share assessments with managers to guide coaching and
            performance reviews.
          </p>
        </div>
      </motion.div>

      {/* Steps Section */}
      <motion.h2
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-4xl  max-md:text-3xl font-bold  pb-8 text-center bg-gradient-to-r from-purple-600 to-cyan-400 text-transparent bg-clip-text"
      >
        <p className="mb-4">How to Use the Platform</p>
        Step-by-Step:
      </motion.h2>

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 mt-9"
      >
        {steps.map((step, index) => (
          <motion.div variants={item} key={index}>
            <Card className="rounded-3xl shadow-xl border border-white/60 bg-white/30 backdrop-blur-md p-6 h-[270px] flex hover:bg-gradient-to-br hover:from-white/40 hover:to-white/20 transition-all duration-300 group">
              <CardContent className="flex flex-col justify-between items-start w-full space-y-4">
                {/* Icon bubble */}
                <div className="bg-white/70 p-4 rounded-full shadow-lg backdrop-blur-sm border border-white/40 transition-transform group-hover:scale-105">
                  {step.icon}
                </div>

                {/* Step Content */}
                <div className="flex flex-col gap-1">
                  <h2 className="text-lg sm:text-xl font-extrabold text-gray-800 leading-snug group-hover:text-purple-700 transition-colors">
                    Step {index + 1}: {step.title}
                  </h2>
                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed group-hover:text-gray-800 transition-colors">
                    {step.description}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* Call to Action */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-28 text-center"
      >
        <h3 className="text-3xl font-semibold text-gray-900 mb-4">
          Ready to Unlock Your Team's Potential?
        </h3>
        <p className="text-gray-600 text-lg mb-8">
          Start now and explore the insights waiting inside your organization.
        </p>
        <button
          className="bg-[#822bda] hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-8 py-4 rounded-full shadow-lg transition-all"
          onClick={() => window.open("https://portal.selfsync.ai/sign-up", "_blank")}
        >
          Get Started
        </button>
      </motion.div>
    </div>
  );
};

export default HowToUse;
